// src/pages/FieldInterventions/CreateIntervention.jsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Paper,
  Grid,
  TextField,
  MenuItem,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  FormHelperText
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Save as SaveIcon
} from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { useAuth } from '../../contexts/AuthContext';
import { useThemeSettings } from '../../contexts/ThemeContext';
import TreeSelect from '../../components/TreeSelect';
import { locationsApi, devicesApi, fieldInterventionsApi } from '../../services/apiClient';

// Helper function to transform locations data for TreeSelect
const transformLocationsForTreeSelect = (locations) => {
  if (!Array.isArray(locations)) return [];

  // Build location tree recursively (same as Tickets.js)
  const buildLocationTree = (locations, parentId = null) => {
    return locations
      .filter((loc) => {
        const locParentId = loc.parentId || loc.parent_id || loc.parentLocation;
        // Handle all falsy values (null, undefined, 0, '', false) as root nodes
        if (parentId === null) {
          return !locParentId || locParentId === 0 || locParentId === '';
        }
        return locParentId === parentId;
      })
      .map((loc) => ({
        id: loc.id || loc._id || loc.location_UUID,
        _id: loc._id || loc.id || loc.location_UUID,
        name: loc.name || loc.friendlyName,
        friendlyName: loc.friendlyName || loc.name,
        locationType: loc.locationType || loc.location_type || 1,
        parentLocation: loc.parentLocation || loc.parent_id || loc.parentId,
        children: buildLocationTree(locations, loc.id || loc._id || loc.location_UUID)
      }));
  };

  // Check if data is already in tree format
  const hasChildren = locations.some((loc) => loc.children && Array.isArray(loc.children));

  if (hasChildren) {
    // Already in tree format, just ensure proper field mapping
    return locations.map((loc) => ({
      id: loc.id || loc._id || loc.location_UUID,
      _id: loc._id || loc.id || loc.location_UUID,
      name: loc.name || loc.friendlyName,
      friendlyName: loc.friendlyName || loc.name,
      locationType: loc.locationType || loc.location_type || 1,
      parentLocation: loc.parentLocation || loc.parent_id || loc.parentId,
      children: loc.children || []
    }));
  } else {
    // Convert flat list to tree
    const tree = buildLocationTree(locations);
    return tree.length > 0 ? tree : locations;
  }
};

const CreateIntervention = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useAuth();
  const { t } = useThemeSettings();

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [locations, setLocations] = useState([]);
  const [locationsForTreeSelect, setLocationsForTreeSelect] = useState([]);
  const [devices, setDevices] = useState([]);
  const [loadingDevices, setLoadingDevices] = useState(false);

  // TreeSelect states
  const [locationTreeSelectOpen, setLocationTreeSelectOpen] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(null);

  const [formData, setFormData] = useState({
    ticket_id: searchParams.get('ticket_id') || null,
    location_id: searchParams.get('location_id') || '',
    device_id: searchParams.get('device_id') || '',
    issue_description: searchParams.get('issue_description') || '',
    scheduled_date: searchParams.get('scheduled_date') || '',
    scheduled_time: searchParams.get('scheduled_time') || '09:00',
    notes: searchParams.get('notes') || '',
    technician_name: user?.username || '',
    status: 'pending'
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    loadLocations();
  }, []);

  useEffect(() => {
    if (formData.location_id) {
      loadDevicesForLocation(formData.location_id);
    } else {
      setDevices([]);
    }
  }, [formData.location_id]);

  const loadLocations = async () => {
    try {
      setLoading(true);

      // Use locationsApi which already has limit: 5000 by default
      const response = await locationsApi.getAll();

      let locationsData = [];
      if (response?.data) {
        if (Array.isArray(response.data)) {
          locationsData = response.data;
        } else if (response.data.data && Array.isArray(response.data.data)) {
          locationsData = response.data.data;
        } else if (response.data.locations && Array.isArray(response.data.locations)) {
          locationsData = response.data.locations;
        }
      }

      // Store both flat and tree-structured locations
      setLocations(locationsData);

      // Transform locations for TreeSelect component
      const treeSelectLocations = transformLocationsForTreeSelect(locationsData);
      setLocationsForTreeSelect(treeSelectLocations);
    } catch (error) {
      console.error('Error loading locations:', error);
      enqueueSnackbar(
        t ? t('FieldInterventions.error_loading_locations') : 'Failed to load locations',
        { variant: 'error' }
      );
    } finally {
      setLoading(false);
    }
  };

  const loadDevicesForLocation = async (locationId) => {
    try {
      setLoadingDevices(true);
      const response = await devicesApi.getAll({ locationId });
      setDevices(response.data.data || response.data.devices || []);
    } catch (error) {
      console.error('Error loading devices:', error);
      setDevices([]);
    } finally {
      setLoadingDevices(false);
    }
  };

  const handleFieldChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleLocationSelect = (location) => {
    setSelectedLocation(location);
    setFormData(prev => ({ ...prev, location_id: location.id }));
    setLocationTreeSelectOpen(false);

    // Clear error for location
    if (errors.location_id) {
      setErrors(prev => ({ ...prev, location_id: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.location_id) {
      newErrors.location_id = t ? t('FieldInterventions.location_required') : 'Location is required';
    }

    if (!formData.issue_description || formData.issue_description.trim().length < 10) {
      newErrors.issue_description = t ? t('FieldInterventions.issue_description_required') : 'Issue description is required (min 10 characters)';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      enqueueSnackbar(
        t ? t('FieldInterventions.validation_errors') : 'Please fix validation errors',
        { variant: 'error' }
      );
      return;
    }

    try {
      setSaving(true);

      // Prepare data with intervention_date
      const dataToSend = {
        ...formData,
        intervention_date: formData.scheduled_date || new Date().toISOString().split('T')[0],
        what_is_wrong: formData.issue_description
      };

      const response = await fieldInterventionsApi.create(dataToSend);
      const data = response.data;
      enqueueSnackbar(
        t ? t('FieldInterventions.intervention_created') : 'Field intervention created successfully',
        { variant: 'success' }
      );

      // Navigate to the created intervention
      navigate(`/field-interventions/${data.intervention.id}`);
    } catch (error) {
      console.error('Error creating intervention:', error);
      enqueueSnackbar(
        error.message || (t ? t('FieldInterventions.error_creating') : 'Failed to create intervention'),
        { variant: 'error' }
      );
    } finally {
      setSaving(false);
    }
  };

  const isFromTicket = !!formData.ticket_id;

  if (loading) {
    return (
      <Container>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ py: 4 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Button startIcon={<BackIcon />} onClick={() => navigate('/field-interventions')}>
            {t ? t('FieldInterventions.back_to_interventions') : 'Back to Interventions'}
          </Button>
          <Button
            variant="contained"
            startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
            onClick={handleSubmit}
            disabled={saving}
          >
            {saving ? (t ? t('Common.please_wait') : 'Saving...') : (t ? t('Common.save') : 'Create Intervention')}
          </Button>
        </Box>

        <Paper sx={{ p: 3 }}>
          <Typography variant="h5" gutterBottom>
            {t ? t('FieldInterventions.create_new_intervention') : 'Create New Field Intervention'}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {t ? t('FieldInterventions.create_intervention_subtitle') : 'Document a field intervention or on-site work'}
          </Typography>

          {isFromTicket && (
            <Alert severity="info" sx={{ mb: 3 }}>
              {t ? t('FieldInterventions.linked_to_ticket') : 'This intervention is linked to a ticket'}
            </Alert>
          )}

          <Grid container spacing={3}>
            {/* Location Selection */}
            <Grid item xs={12} md={6}>
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  {t ? t('FieldInterventions.location') : 'Location'} *
                </Typography>
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={() => setLocationTreeSelectOpen(true)}
                  disabled={isFromTicket && formData.location_id}
                  sx={{
                    justifyContent: 'flex-start',
                    textAlign: 'left',
                    borderColor: errors.location_id ? 'error.main' : undefined
                  }}
                >
                  {selectedLocation ? selectedLocation.friendlyName : (t ? t('Common.select_option') : 'Select a location')}
                </Button>
                {errors.location_id && (
                  <FormHelperText error>{errors.location_id}</FormHelperText>
                )}
                {!errors.location_id && (
                  <FormHelperText>
                    {t ? t('FieldInterventions.location_helper') : 'Select the site or building where the intervention takes place'}
                  </FormHelperText>
                )}
              </Box>
            </Grid>

            {/* Device Selection */}
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>
                  {t ? t('FieldInterventions.device') : 'Device (Optional)'}
                </InputLabel>
                <Select
                  value={formData.device_id}
                  label={t ? t('FieldInterventions.device') : 'Device (Optional)'}
                  onChange={(e) => handleFieldChange('device_id', e.target.value)}
                  disabled={!formData.location_id || loadingDevices}
                >
                  <MenuItem value="">
                    <em>{t ? t('FieldInterventions.no_specific_device') : 'No specific device'}</em>
                  </MenuItem>
                  {devices.map((device) => (
                    <MenuItem key={device.id} value={device.id}>
                      {device.friendlyName || device.name || `Device #${device.id}`}
                      {device.deviceType && ` (${device.deviceType})`}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>
                  {loadingDevices
                    ? (t ? t('Common.loading') : 'Loading devices...')
                    : !formData.location_id
                    ? (t ? t('FieldInterventions.select_location_first') : 'Select a location first')
                    : devices.length === 0
                    ? (t ? t('FieldInterventions.no_devices_at_location') : 'No devices found at this location')
                    : (t ? t('FieldInterventions.device_helper') : 'Select the device if applicable')}
                </FormHelperText>
              </FormControl>
            </Grid>

            {/* Issue Description */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                multiline
                rows={4}
                label={t ? t('FieldInterventions.issue_description') : 'What is going wrong?'}
                value={formData.issue_description}
                onChange={(e) => handleFieldChange('issue_description', e.target.value)}
                error={!!errors.issue_description}
                helperText={errors.issue_description || (t ? t('FieldInterventions.issue_description_helper') : 'Describe the issue or problem that requires intervention (min 10 characters)')}
                placeholder={t ? t('FieldInterventions.issue_description_placeholder') : 'e.g., Camera not working, Network connectivity issues, Hardware malfunction...'}
              />
            </Grid>

            {/* Scheduled Date */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="date"
                label={t ? t('FieldInterventions.scheduled_date') : 'Scheduled Date'}
                value={formData.scheduled_date}
                onChange={(e) => handleFieldChange('scheduled_date', e.target.value)}
                InputLabelProps={{ shrink: true }}
                helperText={t ? t('FieldInterventions.scheduled_date_helper') : 'When is the intervention scheduled?'}
              />
            </Grid>

            {/* Scheduled Time */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="time"
                label={t ? t('FieldInterventions.scheduled_time') : 'Scheduled Time'}
                value={formData.scheduled_time}
                onChange={(e) => handleFieldChange('scheduled_time', e.target.value)}
                InputLabelProps={{ shrink: true }}
                helperText={t ? t('FieldInterventions.scheduled_time_helper') : 'What time is the intervention scheduled?'}
              />
            </Grid>

            {/* Technician Name */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t ? t('FieldInterventions.technician_name') : 'Technician Name'}
                value={formData.technician_name}
                onChange={(e) => handleFieldChange('technician_name', e.target.value)}
                helperText={t ? t('FieldInterventions.technician_name_helper') : 'Name of the technician performing the intervention'}
              />
            </Grid>

            {/* Additional Notes */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label={t ? t('FieldInterventions.additional_notes') : 'Additional Notes'}
                value={formData.notes}
                onChange={(e) => handleFieldChange('notes', e.target.value)}
                placeholder={t ? t('FieldInterventions.additional_notes_placeholder') : 'Add any additional notes or instructions...'}
                helperText={t ? t('FieldInterventions.additional_notes_helper') : 'Any additional information about the intervention'}
              />
            </Grid>
          </Grid>
        </Paper>
      </Box>

      {/* TreeSelect for Location Selection */}
      <TreeSelect
        open={locationTreeSelectOpen}
        onClose={() => setLocationTreeSelectOpen(false)}
        onSelect={handleLocationSelect}
        data={locationsForTreeSelect}
        selectedId={selectedLocation?.id || selectedLocation?._id}
        title={t ? t('FieldInterventions.select_location') : 'Select Location'}
        type="location"
        loading={loading}
        allowNone={false}
      />
    </Container>
  );
};

export default CreateIntervention;
